
import React, { ComponentPropsWithRef, forwardRef, ReactElement, ReactNode, useEffect, useState } from 'react';
import { Grid, GridRowBaseProps } from '../../components';
import { UIComponentWithRef, UIStyledComponentProps } from '../../components/types';
import { SizeVariant } from '../../variants';
import { RadioOptionBase, RadioOptionProps } from './radio-option-base';

type Props = Omit<ComponentPropsWithRef<'input'>, 'size'> & {
  initialValue?: string;
  size?: SizeVariant;
  children?: ReactNode;
};

export type RadioOptionGroupProps = UIStyledComponentProps<Props, 'input'>;

export type RadioOptionGroupComponent<C extends React.ElementType = 'input'> = UIComponentWithRef<C, GridRowBaseProps & Props>;

export const RadioOptionGroup: RadioOptionGroupComponent = forwardRef<HTMLDivElement, GridRowBaseProps & Props>(({
  children,
  className,
  size,
  initialValue,
  onChange,
  ...props
}, ref) => {
  const [value, setValue] = useState(initialValue || null);

  useEffect(() => {
    setValue(initialValue || null);
  }, [initialValue]);

  const onChange_ = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    if (onChange) {
      onChange(event);
    }
    if (event.defaultPrevented) {
      return;
    }
  };

  const mappedChildren = React.Children.map(children || [], (child) => {
    if (!React.isValidElement<RadioOptionProps>(child)) {
      return child;
    }
    const item: ReactElement<RadioOptionProps> = child;
    if (item.type === RadioOptionBase) {
      return React.cloneElement(item, {
        ...child.props,
        size: child.props.size || size,
        checked: child.props.value === value,
        onChange: onChange_,
      });
    }
    return child;
  });

  // !!! ref to group

  return (
    <Grid.Row columns="3" columnGap="0.5rem" rowGap="0.5rem" ref={ref} className={className} {...props}>
      {mappedChildren}
    </Grid.Row>
  );
});

RadioOptionGroup.displayName = 'RadioOptionGroup';
